import React, { useState, useEffect } from "react";
import { Text, ActionButton, Select, Checkbox } from "../custom-components";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../styles/pageConfiguration.css";
import "../styles/dpoDashboard.css";
import { generateTransactionId } from "../utils/transactionId";
import config from "../utils/config";
import { IcEngineeringRequest, IcDatabase, IcSnake, IcPlan, IcFlipVertical, IcTime, IcDataLoan } from "../custom-components/Icon";
import { getUserProfile, fetchBusinessApplication, fetchBusinessApplications, translateConfig, getSystemConfig } from "../store/actions/CommonAction";
import { CLEAR_SESSION } from "../store/constants/Constants";
import { isSandboxMode } from "../utils/sandboxMode";
import "../styles/sessionModal.css";
import "../styles/toast.css";
import { Slide, ToastContainer, toast } from "react-toastify";
import CustomToast from "./CustomToastContainer";
import GuidedTour from "./GuidedTour";
import TourButton from "./TourButton";
import { dpoDashboardTourSteps } from "../utils/tourSteps";

/**
 * Actual API Response Structure:
 * {
 *   totalPurposeCreated: number,
 *   dataTypes: number,
 *   processingActivities: number,
 *   retentionPeriod: number (days),
 *   logs: number,
 *   consentArtefacts: number,
 *   publishedTemp: number,
 *   pendingRenewal: number,
 *   cookiesPublished: number,
 *   cookiesDraft: number,
 *   cookiesInactive: number,
 *   totalConsents: number,
 *   activeConsents: number,
 *   revokedConsents: number,
 *   expiredConsents: number,
 *   notificationSent: number,
 *   notificationEmail: number,
 *   notificationSms: number,
 *   cookiesTotal: number,
 *   cookiesAllAccepted: number,
 *   cookiesPartiallyAccepted: number,
 *   cookiesAllRejected: number,
 *   cookiesNoAction: number,
 *   grievanceTotalRequests: number,
 *   grievanceResolved: number,
 *   grievanceInProgress: number,
 *   grievanceEscalated: number,
 *   grievanceRejected: number,
 *   resolvedSla: number,
 *   exceededSla: number,
 *   grievanceSms: number,
 *   grievanceEmail: number
 * }
 */

const DPODashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantId = useSelector((state) => state.common.tenant_id);
  const sessionToken = useSelector((state) => state.common.session_token);
  const businessId = useSelector((state) => state.common.business_id);
  const userProfile = useSelector((state) => state.common.user_profile);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState("Last 90 days");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Calculate date range based on selected period
  const getDateRange = (period) => {
    const toDate = new Date();
    const fromDate = new Date();
    
    switch(period) {
      case "Last 7 days":
        fromDate.setDate(toDate.getDate() - 7);
        break;
      case "Last 30 days":
        fromDate.setDate(toDate.getDate() - 30);
        break;
      case "Last 60 days":
        fromDate.setDate(toDate.getDate() - 60);
        break;
      case "Last 90 days":
        fromDate.setDate(toDate.getDate() - 90);
        break;
      default:
        // Default to Last 90 days if period doesn't match
        fromDate.setDate(toDate.getDate() - 90);
    }
    
    return {
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: toDate.toISOString().split('T')[0]
    };
  };

  // Fetch DPO Dashboard Data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // In sandbox mode, use default values if credentials aren't ready
      const finalTenantId = tenantId || (isSandboxMode() ? 'sandbox-tenant-id' : '');
      const finalSessionToken = sessionToken || (isSandboxMode() ? 'sandbox-session-token-12345' : '');
      const finalBusinessId = businessId || (isSandboxMode() ? 'sandbox-business-id' : '');
      
      if (!finalTenantId || !finalSessionToken || !finalBusinessId) {
        console.warn("Missing credentials for dashboard fetch");
        if (!isSandboxMode()) {
          setError("Missing credentials. Please log in again.");
          setLoading(false);
          return;
        }
      }
      
      const { fromDate, toDate } = getDateRange(selectedPeriod);
      const txnId = generateTransactionId();
      
      const response = await fetch(
        `${config.dpo_dashboard}?fromDate=${fromDate}&toDate=${toDate}`,
        {
          method: 'GET',
          headers: {
            'txn': txnId,
            'tenant-id': finalTenantId,
            'business-id': finalBusinessId,
            'x-session-token': finalSessionToken,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Dashboard API Response:", JSON.stringify(data, null, 2));
      console.log("Dashboard data structure:", data);
      setDashboardData(data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user profile first, then company logo (which needs businessId)
  useEffect(() => {
    if (!tenantId) {
      console.warn(" Tenant ID is missing in Redux, cannot call API.");
      return;
    }

    console.log(" Tenant ID from Redux:", tenantId);

    const fetchUserProfile = async () => {
      try {
        let res = await dispatch(getUserProfile(tenantId, sessionToken));
        console.log("Response in dashboard profile call", res);

        if (res === 401 || res?.status === 401) {
          // Skip session expiration handling in sandbox mode
          if (isSandboxMode()) {
            console.log('🏖️ SANDBOX MODE - Ignoring 401 error in DPO Dashboard');
            return;
          }
          
          toast.error(
            (props) => (
              <CustomToast
                {...props}
                type="error"
                message={"Session expired"}
              />
            ),
            { icon: false }
          );
          dispatch({ type: CLEAR_SESSION });
          setShowSessionModal(true);
          setTimeout(() => {
            setShowSessionModal(false);
            navigate("/adminLogin");
          }, 7000);
        }
      } catch (error) {
        console.error("Error in profile call", error);

        if (
          error === 401 ||
          error[0]?.errorCode === "JCMP4003" ||
          error[0]?.errorCode === "JCMP4001"
        ) {
          // Skip session expiration handling in sandbox mode
          if (isSandboxMode()) {
            console.log('🏖️ SANDBOX MODE - Ignoring session error in DPO Dashboard');
            return;
          }
          
          toast.error(
            (props) => (
              <CustomToast
                {...props}
                type="error"
                message={"Session expired"}
              />
            ),
            { icon: false }
          );
          dispatch({ type: CLEAR_SESSION });
          setShowSessionModal(true);
          setTimeout(() => {
            setShowSessionModal(false);
            navigate("/adminLogin");
          }, 7000);
        }
      }
    };

    // Fetch user profile first to get businessId
    fetchUserProfile();

  }, [dispatch, tenantId, sessionToken, navigate]);

  // Fetch company logo after businessId is available
  useEffect(() => {
    if (!businessId || !tenantId || !sessionToken) {
      return;
    }

    const fetchCompanyLogo = async () => {
      try {
        console.log('Fetching company logo with businessId:', businessId);
        const response = await dispatch(getSystemConfig());
        const systemConfigInfo = response?.data?.searchList?.[0]?.configurationJson;
        
        if (systemConfigInfo?.logo) {
          try {
            // Store logo as base64 data URL for persistence across page reloads
            let logoDataUrl;
            
            if (systemConfigInfo.logo.startsWith('data:')) {
              // Already a data URL
              logoDataUrl = systemConfigInfo.logo;
            } else {
              // Convert base64 to data URL
              const contentType = systemConfigInfo.logoMeta?.contentType || 'image/png';
              logoDataUrl = `data:${contentType};base64,${systemConfigInfo.logo}`;
            }
            
            // Dispatch logo to Redux
            dispatch({
              type: "SAVE_COMPANY_LOGO",
              payload: logoDataUrl,
            });
            console.log('✅ Company logo saved as base64 data URL');
          } catch (error) {
            console.error("Error processing logo:", error);
            // Fallback: use base64 directly if it's already a data URL
            if (systemConfigInfo.logo.startsWith('data:')) {
              dispatch({
                type: "SAVE_COMPANY_LOGO",
                payload: systemConfigInfo.logo,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching company logo:", error);
      }
    };

    fetchCompanyLogo();
  }, [dispatch, businessId, tenantId, sessionToken]);

  // Fetch business details after business_id is set from user profile
  useEffect(() => {
    if (businessId && sessionToken) {
      console.log("Fetching business details for businessId:", businessId);
      
      const getBusinessDetails = async () => {
        try {
          await dispatch(fetchBusinessApplication(businessId, sessionToken));
          console.log("Business details fetched successfully");
        } catch (error) {
          console.error("Error fetching business details:", error);
        }
      };

      getBusinessDetails();
    }
  }, [businessId, sessionToken, dispatch]);

  useEffect(() => {
    if (userProfile) {
      console.log("📦 User profile from Redux:", userProfile);
      console.log("🏢 Extracted Business ID:", businessId);
    }
  }, [userProfile, businessId]);

  // Fetch business applications
  useEffect(() => {
    dispatch(fetchBusinessApplications(sessionToken, tenantId));
  }, [dispatch, sessionToken, tenantId]);

  // Call translateConfig
  // useEffect(() => {
  //   // Only call translateConfig when businessId and token are available
  //   if (!businessId || !sessionToken || !tenantId) {
  //     console.log("Waiting for businessId, token, or tenantId...", { businessId, sessionToken, tenantId });
  //     return;
  //   }

  //   const callTranslateConfig = async () => {
  //     try {
  //       console.log("Business ID: in use effect", businessId);
        
  //       const data = await translateConfig(tenantId, businessId, sessionToken);
  //       console.log("Response from translateConfig:", data);
  //     } catch (error) {
  //       console.error("Failed to call translateConfig:", error);
  //     } 
  //   };

  //   callTranslateConfig();
  // }, [businessId, sessionToken, tenantId]);

  // Fetch DPO Dashboard Data
  useEffect(() => {
    console.log("DPO Dashboard mounted or dependencies changed", { tenantId, sessionToken, businessId, selectedPeriod });
    
    // Reset loading state when navigating back to dashboard
    if (!isInitialLoad && dashboardData === null) {
      setLoading(true);
    }
    
    // In sandbox mode, use default values if credentials aren't ready
    const finalTenantId = tenantId || (isSandboxMode() ? 'sandbox-tenant-id' : null);
    const finalSessionToken = sessionToken || (isSandboxMode() ? 'sandbox-session-token-12345' : null);
    const finalBusinessId = businessId || (isSandboxMode() ? 'sandbox-business-id' : null);
    
    if (finalTenantId && finalSessionToken && finalBusinessId) {
      // Use a small delay to ensure state is ready, especially when navigating back
      const timer = setTimeout(() => {
      fetchDashboardData();
        setIsInitialLoad(false);
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isSandboxMode()) {
      // In non-sandbox mode, wait for credentials
      setLoading(false);
    }
  }, [tenantId, sessionToken, businessId, selectedPeriod]);

  // Donut Chart Component
  const DonutChart = ({ data, total, centerLabel, size = 200, showPercentages = false }) => {
    const outerRadius = 85;
    const innerRadius = 70;
    const centerX = 100;
    const centerY = 100;
    
    let currentAngle = -90; // Start from top
    const segments = [];
    const percentageLabels = [];
    
    // Check if there's any data with percentage > 0
    const hasData = data && data.length > 0 && data.some(item => item.percentage > 0);
    
    // Create all slices first
    data && data.forEach((item, index) => {
      if (item.percentage === 0) return;
      
      const percentage = item.percentage;
      const color = item.color;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      const midAngle = startAngle + angle / 2;
      
      // Convert to radians
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;
      const midRad = (midAngle * Math.PI) / 180;
      
      // Outer arc points
      const x1Outer = centerX + outerRadius * Math.cos(startRad);
      const y1Outer = centerY + outerRadius * Math.sin(startRad);
      const x2Outer = centerX + outerRadius * Math.cos(endRad);
      const y2Outer = centerY + outerRadius * Math.sin(endRad);
      
      // Inner arc points
      const x1Inner = centerX + innerRadius * Math.cos(startRad);
      const y1Inner = centerY + innerRadius * Math.sin(startRad);
      const x2Inner = centerX + innerRadius * Math.cos(endRad);
      const y2Inner = centerY + innerRadius * Math.sin(endRad);
      
      const largeArc = angle > 180 ? 1 : 0;
      
      // Create donut segment path
      const path = [
        `M ${x1Outer} ${y1Outer}`,
        `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2Outer} ${y2Outer}`,
        `L ${x2Inner} ${y2Inner}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1Inner} ${y1Inner}`,
        'Z'
      ].join(' ');
      
      segments.push(
        <path key={`slice-${index}`} d={path} fill={color} />
      );
      
      // Calculate label position (outside the donut) - only show if > 5% to avoid crowding
      if (showPercentages && percentage >= 5) {
        const labelRadius = outerRadius + 15;
        const labelX = centerX + labelRadius * Math.cos(midRad);
        const labelY = centerY + labelRadius * Math.sin(midRad);
        percentageLabels.push(
          <text
            key={`label-${index}`}
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fontWeight="700"
            fill={color}
          >
            {percentage}%
          </text>
        );
      }
      
      currentAngle = endAngle;
    });

    // Create grey background donut (always visible)
    const greyDonutPath = (() => {
      const outerCircle = `M ${centerX + outerRadius} ${centerY} A ${outerRadius} ${outerRadius} 0 1 1 ${centerX - outerRadius} ${centerY} A ${outerRadius} ${outerRadius} 0 1 1 ${centerX + outerRadius} ${centerY}`;
      const innerCircle = `M ${centerX + innerRadius} ${centerY} A ${innerRadius} ${innerRadius} 0 1 0 ${centerX - innerRadius} ${centerY} A ${innerRadius} ${innerRadius} 0 1 0 ${centerX + innerRadius} ${centerY}`;
      return `${outerCircle} ${innerCircle} Z`;
    })();

  return (
      <div className="donut-chart" style={{ width: size, height: size }}>
        <svg viewBox="0 0 200 200" width={size} height={size} style={{ overflow: 'visible' }}>
          {/* Always show grey background donut */}
          <path 
            d={greyDonutPath}
            fill="#f5f5f5" 
            stroke="#e5e5e5" 
            strokeWidth="1"
            fillRule="evenodd"
          />
          
          {hasData && (
            <>
              <g>{segments}</g>
              {showPercentages && <g>{percentageLabels}</g>}
            </>
          )}
        </svg>
        {(total || centerLabel) && (
          <div className="donut-center">
            {total && <div className="donut-center-value">{total}</div>}
            {centerLabel && <div className="donut-center-label">{centerLabel}</div>}
          </div>
        )}
      </div>
    );
  };

  // Helper function to format retention period from days to human-readable format
  // Handles both API response format (e.g., "10 year", "10 month") and numeric days
  const formatRetentionPeriod = (value) => {
    if (!value || value === 'N/A' || value === 0) return 'N/A';
    
    // Check if value is already a formatted string (contains year/month/day)
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().trim();
      
      // Check if it already contains time unit keywords
      if (lowerValue.includes('year') || lowerValue.includes('month') || lowerValue.includes('day')) {
        // Return as-is, but normalize pluralization if needed
        // API might return "10 year" or "10 years", we'll keep it as-is
        return value.trim();
      }
      
      // If it's a string but doesn't contain time units, try to parse as number
      const daysNum = parseInt(value, 10);
      if (!isNaN(daysNum)) {
        // Convert days to years, months, or days
        const years = Math.floor(daysNum / 365);
        const months = Math.floor((daysNum % 365) / 30);
        const remainingDays = daysNum % 30;
        
        if (years > 0) {
          return `${years} ${years === 1 ? 'year' : 'years'}`;
        } else if (months > 0) {
          return `${months} ${months === 1 ? 'month' : 'months'}`;
        } else {
          return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
        }
      }
    }
    
    // If value is a number, convert from days
    if (typeof value === 'number') {
      const daysNum = value;
      const years = Math.floor(daysNum / 365);
      const months = Math.floor((daysNum % 365) / 30);
      const remainingDays = daysNum % 30;
      
      if (years > 0) {
        return `${years} ${years === 1 ? 'year' : 'years'}`;
      } else if (months > 0) {
        return `${months} ${months === 1 ? 'month' : 'months'}`;
      } else {
        return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
      }
    }
    
    // If we can't parse it, return as-is or 'N/A'
    return value || 'N/A';
  };

  // Helper functions to safely extract data from API response
  const getData = (path, defaultValue = 0) => {
    if (!dashboardData) return defaultValue;
    
    // Handle direct access (no dots in path)
    if (!path.includes('.')) {
      const value = dashboardData[path];
      // Explicitly check for null/undefined, but allow 0 and other falsy numbers
      return value !== null && value !== undefined ? value : defaultValue;
    }
    
    // Handle nested path access
    const value = path.split('.').reduce((obj, key) => {
      return obj && obj[key] !== undefined ? obj[key] : null;
    }, dashboardData);
    
    // Explicitly check for null/undefined, but allow 0 and other falsy numbers
    return value !== null && value !== undefined ? value : defaultValue;
  };

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  const renderDonutChart = (values, colors) => {
    const total = values.reduce((a, b) => a + b, 0);
    const C = 251.2;
    let currentOffset = 0;
    
    if (total === 0) {
      return <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f1f5f9" strokeWidth="16" />;
    }

    return (
      <>
        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f1f5f9" strokeWidth="16" />
        {values.map((val, idx) => {
          if (val === 0) return null;
          const strokeLength = (val / total) * C;
          const offset = currentOffset;
          currentOffset += strokeLength;
          
          return (
            <circle 
              key={idx}
              cx="50" cy="50" r="40" 
              fill="transparent" 
              stroke={colors[idx]} 
              strokeWidth="16" 
              strokeDasharray={`${strokeLength} ${C}`} 
              strokeDashoffset={-offset} 
            />
          );
        })}
      </>
    );
  };

  // Helper to get the entire data object (for debugging)
  const getApiData = () => dashboardData || {};

  // Loading state
  if (loading) {
    return (
      <div className="configurePage">
        <div className="dpo-dashboard-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <div style={{ fontSize: '18px', color: '#666' }}>Loading dashboard data...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="configurePage">
        <div className="dpo-dashboard-container">
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '400px', gap: '16px' }}>
            <div style={{ fontSize: '18px', color: '#f44336' }}>Error loading dashboard data</div>
            <div style={{ fontSize: '14px', color: '#666' }}>{error}</div>
            <button 
              onClick={fetchDashboardData} 
              style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Don't show session modal in sandbox mode */}
      {showSessionModal && !isSandboxMode() && (
        <div className="session-timeout-overlay">
          <div className="session-timeout-modal">
            <h2 className="session-timeout-title">Session Expiring Soon</h2>
            <p className="session-timeout-message">
              Your session will expire in {Math.floor(sessionTimeLeft / 60)}:{(sessionTimeLeft % 60).toString().padStart(2, '0')} minutes due to inactivity.
            </p>
            <div className="session-timeout-actions">
              <button 
                className="session-timeout-btn session-timeout-btn-logout"
                onClick={handleLogout}
              >
                Logout
              </button>
              <button 
                className="session-timeout-btn session-timeout-btn-continue"
                onClick={handleContinueSession}
              >
                Continue Session
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="configurePage">
        <div className="dpo-dashboard-container">
          


          {/* Global Actions */}
          <div className="global-actions-bar">
            <select className="period-dropdown" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
              <option>Last 30 days</option>
              <option>Last 60 days</option>
              <option>Last 90 days</option>
            </select>
            <button className="audit-btn" onClick={() => navigate('/audit-compliance')}>
              <span style={{ fontWeight: '600' }}>Audit & reporting</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
            </button>
          </div>

          {/* Top Metrics Grid */}
          <div className="top-metrics-grid" data-tour="stats-section">
            <div className="metric-card">
              <div className="metric-card-left">
                <div className="metric-icon-box green">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{getData('totalPurposeCreated', 14)}</div>
                  <div className="metric-label">Total purposes created</div>
                </div>
              </div>
              <div className="metric-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-card-left">
                <div className="metric-icon-box blue">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{getData('dataTypes', 10)}</div>
                  <div className="metric-label">Data Types & items</div>
                </div>
              </div>
              <div className="metric-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-card-left">
                <div className="metric-icon-box purple">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="9 12 11 14 15 10"></polyline></svg>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{getData('processingActivities', 0)}</div>
                  <div className="metric-label">Processing Activities</div>
                </div>
              </div>
              <div className="metric-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-card-left">
                <div className="metric-icon-box orange">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{getData('dataProcessor', 1)}</div>
                  <div className="metric-label">Data Processor</div>
                </div>
              </div>
              <div className="metric-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </div>
            </div>
          </div>

          {/* Bottom Grid */}
          <div className="bottom-dashboard-grid">
            
            {/* Consent Status */}
            <div className="dashboard-panel" data-tour="consent-section" onClick={() => navigate('/templates')} style={{ cursor: 'pointer' }}>
              <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="panel-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="9 12 11 14 15 10"></polyline></svg>
                  Consent Status
                </div>
                <div className="metric-arrow" style={{ color: '#94a3b8' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </div>
              
              <div className="consent-status-content">
                <div className="donut-chart-wrapper">
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    {renderDonutChart(
                      [getData('activeConsents', 8), getData('pendingRenewal', 2), getData('revokedConsents', 1), getData('expiredConsents', 1)],
                      ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444']
                    )}
                  </svg>
                  <div className="donut-center">
                    <div className="val">{getData('totalConsents', 12)}</div>
                    <div className="lbl">Total Consents</div>
                  </div>
                </div>

                <div className="status-legend">
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-active"></div><span className="legend-name">Active</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('activeConsents', 8)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('activeConsents', 8), getData('totalConsents', 12))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-pending"></div><span className="legend-name">Pending Renewal</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('pendingRenewal', 2)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('pendingRenewal', 2), getData('totalConsents', 12))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-revoked"></div><span className="legend-name">Revoked</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('revokedConsents', 1)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('revokedConsents', 1), getData('totalConsents', 12))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-expired"></div><span className="legend-name">Expired</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('expiredConsents', 1)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('expiredConsents', 1), getData('totalConsents', 12))}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cookie Consents */}
            <div className="dashboard-panel" data-tour="cookies-section" onClick={() => navigate('/registercookies')} style={{ cursor: 'pointer' }}>
              <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="panel-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                  Cookie Consents
                </div>
                <div className="metric-arrow" style={{ color: '#94a3b8' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </div>
              
              <div className="consent-status-content">
                <div className="donut-chart-wrapper">
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    {renderDonutChart(
                      [getData('cookiesAllAccepted', 0), getData('cookiesPartiallyAccepted', 0), getData('cookiesNoAction', 0), getData('cookiesAllRejected', 0)],
                      ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444']
                    )}
                  </svg>
                  <div className="donut-center">
                    <div className="val">{getData('cookiesTotal', 0)}</div>
                    <div className="lbl">Total Consents</div>
                  </div>
                </div>

                <div className="status-legend">
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-green"></div><span className="legend-name">All accepted</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('cookiesAllAccepted', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('cookiesAllAccepted', 0), getData('cookiesTotal', 0))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-blue"></div><span className="legend-name">Partially accepted</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('cookiesPartiallyAccepted', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('cookiesPartiallyAccepted', 0), getData('cookiesTotal', 0))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-orange"></div><span className="legend-name">No action</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('cookiesNoAction', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('cookiesNoAction', 0), getData('cookiesTotal', 0))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-red"></div><span className="legend-name">All rejected</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('cookiesAllRejected', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('cookiesAllRejected', 0), getData('cookiesTotal', 0))}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Grievance Requests */}
            <div className="dashboard-panel" data-tour="grievance-section" onClick={() => navigate('/request')} style={{ cursor: 'pointer' }}>
              <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="panel-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                  Grievance Requests
                </div>
                <div className="metric-arrow" style={{ color: '#94a3b8' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </div>
              
              <div className="consent-status-content">
                <div className="donut-chart-wrapper">
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    {renderDonutChart(
                      [getData('grievanceResolved', 0), getData('grievanceInProgress', 0), getData('grievanceEscalated', 0), getData('grievanceRejected', 0)],
                      ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444']
                    )}
                  </svg>
                  <div className="donut-center">
                    <div className="val">{getData('grievanceTotalRequests', 0)}</div>
                    <div className="lbl">Total Grievances</div>
                  </div>
                </div>

                <div className="status-legend">
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-green"></div><span className="legend-name">Resolved</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('grievanceResolved', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('grievanceResolved', 0), getData('grievanceTotalRequests', 0))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-blue"></div><span className="legend-name">In Progress</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('grievanceInProgress', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('grievanceInProgress', 0), getData('grievanceTotalRequests', 0))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-orange"></div><span className="legend-name">Escalated</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('grievanceEscalated', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('grievanceEscalated', 0), getData('grievanceTotalRequests', 0))}%</span>
                    </div>
                  </div>
                  <div className="legend-row">
                    <div className="legend-left"><div className="legend-dot dot-red"></div><span className="legend-name">Rejected</span></div>
                    <div className="legend-right">
                      <span className="legend-count">{getData('grievanceRejected', 0)}</span>
                      <span className="legend-percent">{calculatePercentage(getData('grievanceRejected', 0), getData('grievanceTotalRequests', 0))}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Notifications Section */}
          <div className="notifications-section-title">Notifications</div>
          <div className="notifications-grid" data-tour="notifications-section">
            {/* Consent Notifications */}
            <div className="dashboard-panel" onClick={() => navigate('/notification-templates')} style={{ cursor: 'pointer' }}>
              <div className="panel-header" style={{ paddingBottom: '0', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="panel-title" style={{ fontSize: '13px', color: '#64748b' }}>
                  Consent
                  <svg style={{ marginLeft: '4px', verticalAlign: 'middle' }} width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="12"></circle></svg>
                </div>
                <div className="metric-arrow" style={{ color: '#94a3b8' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </div>
              <div className="notification-stats">
                <div className="notif-stat">
                  <div className="notif-icon email">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </div>
                  <div className="notif-details">
                    <span className="notif-count">{getData('notificationEmail', 0)}</span>
                    <span className="notif-label">Email</span>
                  </div>
                </div>
                <div className="notif-stat">
                  <div className="notif-icon sms">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                  </div>
                  <div className="notif-details">
                    <span className="notif-count">{getData('notificationSms', 0)}</span>
                    <span className="notif-label">SMS</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Grievance Notifications */}
            <div className="dashboard-panel" onClick={() => navigate('/grievanceFormTemplates')} style={{ cursor: 'pointer' }}>
              <div className="panel-header" style={{ paddingBottom: '0', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="panel-title" style={{ fontSize: '13px', color: '#64748b' }}>
                  Grievance
                  <svg style={{ marginLeft: '4px', verticalAlign: 'middle' }} width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="12"></circle></svg>
                </div>
                <div className="metric-arrow" style={{ color: '#94a3b8' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </div>
              <div className="notification-stats">
                <div className="notif-stat">
                  <div className="notif-icon email">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </div>
                  <div className="notif-details">
                    <span className="notif-count">{getData('grievanceEmail', 0)}</span>
                    <span className="notif-label">Email</span>
                  </div>
                </div>
                <div className="notif-stat">
                  <div className="notif-icon sms">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                  </div>
                  <div className="notif-details">
                    <span className="notif-count">{getData('grievanceSms', 0)}</span>
                    <span className="notif-label">SMS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        closeButton={false}
        toastClassName={() => "toast-wrapper"}
        transition={Slide}
      />
      
      {/* Guided Tour */}
      <GuidedTour 
        steps={dpoDashboardTourSteps} 
        tourId="dpo-dashboard-tour"
        showOnFirstVisit={true}
        onComplete={() => console.log('DPO Dashboard tour completed!')}
      />
      
      {/* Tour Help Button */}
      <TourButton tourId="dpo-dashboard-tour" label="Start Tour" position="bottom-right" />
    </>
  );
};

export default DPODashboard;


